import { useCallback, useRef, useState } from 'react';

import { type UseMutationOptions, useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import {
  useFileAccessMutation,
  useFileAccessMutationLegacy,
} from '@/features/candidate/useCandidateTableMutation';
import type {
  AccessRequest,
  UploadFileRequest,
  UploadFileResponse,
  UploadUrlPostResponse,
} from '@/services/api';
import { uploadObjectToS3 } from '@/utils/s3';

export type FileUploadVariablesLegacy = {
  access: AccessRequest;
  file: File | Blob;
};

export const useUploadFileMutationLegacy = (
  options?: UseMutationOptions<
    UploadUrlPostResponse,
    AxiosError,
    FileUploadVariablesLegacy
  >
) => {
  const [progress, setProgress] = useState(1);
  const abortControllerRef = useRef<AbortController | null>(null);
  const fileAccessMutation = useFileAccessMutationLegacy({
    disableDefaultErrorHandler: true,
  });

  const mutation = useMutation({
    mutationFn: async ({ access: accessReq, file }) => {
      abortControllerRef.current = new AbortController();
      setProgress(1);

      const access = await fileAccessMutation.mutateAsync(accessReq);

      await uploadObjectToS3({
        blob: file,
        uploadUrl: access.signedUrl!,
        headers: access.requestHeaders as Record<string, string>,
        onProgress: (event) => {
          const _progress = Math.round((event.loaded / event.total) * 100);
          setProgress(_progress);
        },
        signal: abortControllerRef.current?.signal,
      });

      return access;
    },
    ...options,
  });

  const reset = useCallback(() => {
    abortControllerRef.current?.abort();
    mutation.reset();
    setProgress(0);
  }, [mutation]);

  return {
    ...mutation,
    reset,
    abortControllerRef,
    uploadProgress: progress,
  };
};

export type FileUploadVariables = {
  access: UploadFileRequest;
  file: File | Blob;
};

export const useUploadFileMutation = (
  options?: UseMutationOptions<
    UploadFileResponse,
    AxiosError,
    FileUploadVariables
  >
) => {
  const [progress, setProgress] = useState(1);
  const abortControllerRef = useRef<AbortController | null>(null);
  const fileAccessMutation = useFileAccessMutation({
    disableDefaultErrorHandler: true,
  });

  const mutation = useMutation({
    mutationFn: async ({ access: accessReq, file }) => {
      abortControllerRef.current = new AbortController();
      setProgress(1);

      const access = await fileAccessMutation.mutateAsync(accessReq);

      await uploadObjectToS3({
        blob: file,
        uploadUrl: access.signedUrl!,
        onProgress: (event) => {
          const _progress = Math.round((event.loaded / event.total) * 100);
          setProgress(_progress);
        },
        signal: abortControllerRef.current?.signal,
      });

      return access;
    },
    ...options,
  });

  const reset = useCallback(() => {
    abortControllerRef.current?.abort();
    mutation.reset();
    setProgress(0);
  }, [mutation]);

  return {
    ...mutation,
    reset,
    abortControllerRef,
    uploadProgress: progress,
  };
};
